// src/pages/performance/ManageAppraisalsAppraisalsTab.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Divider,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";

import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TuneIcon from "@mui/icons-material/Tune";
import SearchIcon from "@mui/icons-material/Search";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EmailIcon from "@mui/icons-material/Email";

import StatusChip from "./ManageAppraisalsStatusChip";
import {
  API,
  CYCLE_OPTIONS,
  PERIOD_OPTIONS,
  STATUS_OPTIONS,
  WORKFLOW_TEMPLATE_OPTIONS_FALLBACK,
} from "./manageAppraisals.constants";
import {
  cleanParams,
  normalizePageResponse,
  rowFromValueGetterArgs,
  safeMsg,
  workflowLabel,
} from "./manageAppraisals.utils";
import {
  BulkCreateAppraisalsDialog,
  CreateAppraisalDialog,
  DeleteAppraisalDialog,
  EditAppraisalDialog,
} from "./ManageAppraisalsAppraisalDialogs";
import { AppraisalDetailsDrawer } from "./ManageAppraisalsDetailsDrawer";

const EMAIL_DISPATCH_ENDPOINT = "/performance/emails/dispatch";

const NoRowsOverlay = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      py: 7,
    }}
  >
    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 900 }}>
      No appraisals found
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Adjust filters or create a new appraisal.
    </Typography>
  </Box>
);

export default function AppraisalsTab({
  workflowTemplateOptions = WORKFLOW_TEMPLATE_OPTIONS_FALLBACK,
  searchQuery = "",
  onSearchQueryChange,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const initialSearch = String(searchQuery || "").trim();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [actionBusyId, setActionBusyId] = useState(null);

  const [draft, setDraft] = useState({
    search: initialSearch,
    status: "",
    period: "",
    cycle: "",
    workflow_template_slug: "",
    department: "",
    role: "",
    customized_only: false,
  });

  const [applied, setApplied] = useState({
    search: initialSearch,
    status: "",
    period: "",
    cycle: "",
    workflow_template_slug: "",
    department: "",
    role: "",
    customized_only: false,
  });

  /* ------------------------------------------------------------------ */
  /* URL Search Param Sync (q) */
  /* ------------------------------------------------------------------ */
  const externalSearch = useMemo(() => String(searchQuery || "").trim(), [searchQuery]);
  const lastExternalRef = useRef(externalSearch);

  useEffect(() => {
    if (lastExternalRef.current === externalSearch) return;
    lastExternalRef.current = externalSearch;

    setDraft((p) => ({ ...p, search: externalSearch }));
    setApplied((p) => ({ ...p, search: externalSearch }));
    setPage(0);
  }, [externalSearch]);

  const applyFilters = useCallback(() => {
    setError("");
    setSuccess("");
    setPage(0);

    const nextSearch = String(draft.search || "").trim();
    setApplied({ ...draft, search: nextSearch });
    onSearchQueryChange?.(nextSearch);
  }, [draft, onSearchQueryChange]);

  const resetFilters = useCallback(() => {
    const next = {
      search: "",
      status: "",
      period: "",
      cycle: "",
      workflow_template_slug: "",
      department: "",
      role: "",
      customized_only: false,
    };
    setDraft(next);
    setApplied(next);
    setPage(0);
    onSearchQueryChange?.("");
  }, [onSearchQueryChange]);

  const clearSearch = useCallback(() => {
    setDraft((p) => ({ ...p, search: "" }));
    // apply empty search immediately
    setApplied((p) => ({ ...p, search: "" }));
    setPage(0);
    onSearchQueryChange?.("");
  }, [onSearchQueryChange]);
  const displayRows = rows;
  const displayRowCount = rowCount;

  /* ------------------------------------------------------------------ */
  /* Fetch */
  /* ------------------------------------------------------------------ */
  const fetchAppraisals = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = cleanParams({
        per_page: pageSize,
        page: page + 1,
        status: applied.status || undefined,
        search: applied.search || undefined,
        period: applied.period || undefined,
        cycle: applied.cycle || undefined,
        workflow_template_slug: applied.workflow_template_slug || undefined,
        department: applied.department || undefined,
        role: applied.role || undefined,
        customized_only: applied.customized_only ? 1 : undefined,
      });

      const res = await api.get(API.appraisals, { params });
      const { rows: normalizedRows, total } = normalizePageResponse(res);

      setRows(Array.isArray(normalizedRows) ? normalizedRows : []);
      setRowCount(Number.isFinite(total) ? total : 0);
    } catch (err) {
      setError(safeMsg(err, "Failed to load appraisals"));
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [applied, page, pageSize]);

  useEffect(() => {
    fetchAppraisals();
  }, [fetchAppraisals]);

  /* ------------------------------------------------------------------ */
  /* Actions */
  /* ------------------------------------------------------------------ */
  const getAppraisalId = useCallback((row) => row?.id ?? row?.appraisal_id ?? null, []);

  const openAppraisalPage = useCallback(
    (row) => {
      const id = getAppraisalId(row);
      if (!id) return;
      navigate(`/performance/appraisals/${id}`);
    },
    [navigate, getAppraisalId]
  );

  const dispatchEmailsForAppraisal = useCallback(
    async (row) => {
      const id = getAppraisalId(row);
      if (!id) return;

      setError("");
      setSuccess("");
      setActionBusyId(id);

      try {
        await api.post(EMAIL_DISPATCH_ENDPOINT, { appraisal_id: id });
        setSuccess(`Emails dispatched for appraisal #${id}`);
      } catch (err) {
        setError(safeMsg(err, "Failed to dispatch emails"));
      } finally {
        setActionBusyId(null);
      }
    },
    [getAppraisalId]
  );

  /* ------------------------------------------------------------------ */
  /* Columns (simple / readable) */
  /* ------------------------------------------------------------------ */
  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 88 },

      {
        field: "employee",
        headerName: "Employee",
        flex: 1,
        minWidth: 240,
        renderCell: (params) => {
          const row = params.row;
          const emp = row?.employee || row?.employee_user || row?.user || {};
          const name = emp?.name || row?.employee_name || emp?.full_name || "—";
          const code = emp?.employee_code || row?.employee_code || emp?.code || "";

          return (
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.15 }} noWrap>
                {name}
              </Typography>
              {!!code && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {code}
                </Typography>
              )}
            </Stack>
          );
        },
      },

      {
        field: "department",
        headerName: "Department",
        width: 200,
        valueGetter: (a, b) => {
          const row = rowFromValueGetterArgs(a, b);
          return (
            row?.employee?.department_name ??
            row?.employee?.department ??
            row?.department_name ??
            row?.department ??
            "—"
          );
        },
      },

      {
        field: "workflow",
        headerName: "Workflow",
        width: 260,
        valueGetter: (a, b) => {
          const row = rowFromValueGetterArgs(a, b);
          return (
            row?.workflowTemplate?.name ||
            row?.workflow_template?.name ||
            row?.workflow_name ||
            "—"
          );
        },
      },

      {
        field: "status",
        headerName: "Status",
        width: 150,
        renderCell: (params) => <StatusChip value={params.value} />,
      },

      {
        field: "current_step_order",
        headerName: "Step",
        width: 110,
        valueGetter: (a, b) => {
          const row = rowFromValueGetterArgs(a, b);
          return row?.current_step_order ?? row?.currentStepOrder ?? "—";
        },
      },

      {
        field: "actions",
        headerName: "Actions",
        width: 230,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const id = params.row?.id ?? params.row?.appraisal_id ?? null;
          const isBusy = !!id && actionBusyId === id;

          return (
            <Stack direction="row" spacing={0.25} justifyContent="center">
              <Tooltip title="Details">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(id);
                    setDetailsOpen(true);
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Open">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openAppraisalPage(params.row);
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Send emails">
                <span>
                  <IconButton
                    size="small"
                    disabled={isBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatchEmailsForAppraisal(params.row);
                    }}
                  >
                    {isBusy ? <CircularProgress size={18} /> : <EmailIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  color="info"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(id);
                    setEditOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(id);
                    setDeleteOpen(true);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [actionBusyId, dispatchEmailsForAppraisal, openAppraisalPage]
  );

  /* ------------------------------------------------------------------ */
  /* Quick counts */
  /* ------------------------------------------------------------------ */
  const quickCounts = useMemo(() => {
    const inProgress = (displayRows || []).filter((r) => String(r.status) === "in_progress").length;
    const completed = (displayRows || []).filter((r) => String(r.status) === "completed").length;
    return { inProgress, completed };
  }, [displayRows]);

  /* ------------------------------------------------------------------ */
  /* Dynamic filter options (from loaded rows) */
  /* ------------------------------------------------------------------ */
  const deptOptions = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((r) => {
      const d =
        r?.employee?.department_name ??
        r?.employee?.department ??
        r?.department_name ??
        r?.department;
      if (d && String(d).trim() !== "") set.add(String(d).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const roleOptions = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((r) => {
      const v = r?.employee?.actual_role ?? r?.actual_role ?? r?.role;
      if (v && String(v).trim() !== "") set.add(String(v).trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  /* ------------------------------------------------------------------ */
  /* Active chips (simple) */
  /* ------------------------------------------------------------------ */
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (applied.status) chips.push({ k: "status", label: `Status: ${applied.status}` });
    if (applied.period) chips.push({ k: "period", label: `Period: ${applied.period}` });
    if (applied.cycle) chips.push({ k: "cycle", label: `Cycle: ${applied.cycle}` });
    if (applied.workflow_template_slug) {
      chips.push({
        k: "workflow_template_slug",
        label: `Workflow: ${workflowLabel(applied.workflow_template_slug, workflowTemplateOptions)}`,
      });
    }
    if (applied.department) chips.push({ k: "department", label: `Dept: ${applied.department}` });
    if (applied.role) chips.push({ k: "role", label: `Role: ${applied.role}` });
    if (applied.customized_only) chips.push({ k: "customized_only", label: "Customized only" });
    return chips;
  }, [applied, workflowTemplateOptions]);

  const clearChip = useCallback(
    (k) => {
      const patch = { [k]: k === "customized_only" ? false : "" };
      setDraft((p) => ({ ...p, ...patch }));
      setApplied((p) => ({ ...p, ...patch }));
      setPage(0);
    },
    []
  );

  /* ------------------------------------------------------------------ */
  /* Pagination control */
  /* ------------------------------------------------------------------ */
  const currentPaginationModel = { page, pageSize };

  const handlePaginationChange = useCallback(
    (model) => {
      setPage(model.page);
      setPageSize(model.pageSize);
    },
    []
  );

  /* ------------------------------------------------------------------ */
  /* UI */
  /* ------------------------------------------------------------------ */
  return (
    <Box>
      {/* Top controls (simple + clear) */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
          boxShadow: "none",
          overflow: "hidden",
          mb: 1.5,
        }}
      >
        <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
          <Stack spacing={1.5}>
            {/* Search row */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <TextField
                size="small"
                label="Search"
                value={draft.search}
                onChange={(e) => setDraft((p) => ({ ...p, search: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applyFilters();
                }}
                placeholder="Employee name / code / workflow / status"
                sx={{
                  width: { xs: "100%", md: 520 },
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: draft.search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch} aria-label="Clear">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                helperText="Press Enter to apply"
              />

              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<GroupAddIcon />}
                  onClick={() => setBulkOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 900, borderRadius: "8px" }}
                >
                  Bulk Create
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 900, borderRadius: "8px", boxShadow: "none" }}
                >
                  Create
                </Button>
              </Stack>
            </Stack>

            {/* Filter row */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={draft.status}
                  onChange={(e) => setDraft((p) => ({ ...p, status: e.target.value }))}
                  sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  SelectProps={{ MenuProps: { PaperProps: { sx: { borderRadius: "8px" } } } }}
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUS_OPTIONS.filter((opt) => opt.value !== "").map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Tooltip title={filtersOpen ? "Hide advanced filters" : "Show advanced filters"}>
                  <IconButton onClick={() => setFiltersOpen((s) => !s)} aria-label="Advanced filters">
                    <TuneIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Refresh">
                  <IconButton onClick={fetchAppraisals} aria-label="Refresh">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                <Button
                  variant="text"
                  onClick={resetFilters}
                  sx={{ textTransform: "none", fontWeight: 900, borderRadius: "8px" }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  sx={{
                    textTransform: "none",
                    fontWeight: 900,
                    borderRadius: "8px",
                    boxShadow: "none",
                  }}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>

            {/* Advanced Filters */}
            <Collapse in={filtersOpen}>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    size="small"
                    label="Period / Year"
                    value={draft.period}
                    onChange={(e) => setDraft((p) => ({ ...p, period: e.target.value }))}
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {PERIOD_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    size="small"
                    label="Cycle"
                    value={draft.cycle}
                    onChange={(e) => setDraft((p) => ({ ...p, cycle: e.target.value }))}
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {CYCLE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    size="small"
                    label="Workflow Template"
                    value={draft.workflow_template_slug}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, workflow_template_slug: e.target.value }))
                    }
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {workflowTemplateOptions.map((opt) => (
                      <MenuItem key={opt.value || "auto"} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!draft.customized_only}
                        onChange={(e) => setDraft((p) => ({ ...p, customized_only: e.target.checked }))}
                      />
                    }
                    label="Customized only"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={deptOptions}
                    value={draft.department || null}
                    onChange={(_, val) => setDraft((p) => ({ ...p, department: val || "" }))}
                    freeSolo
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Department"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={roleOptions}
                    value={draft.role || null}
                    onChange={(_, val) => setDraft((p) => ({ ...p, role: val || "" }))}
                    freeSolo
                    clearOnEscape
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Role"
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Collapse>

            {/* Chips + quick stats */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`Showing: ${displayRows.length} of ${rowCount}`}
                sx={{
                  fontWeight: 900,
                  borderRadius: "8px",
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                }}
              />
              <Chip
                size="small"
                label={`In progress: ${quickCounts.inProgress}`}
                sx={{
                  fontWeight: 900,
                  borderRadius: "8px",
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                }}
              />
              <Chip
                size="small"
                label={`Completed: ${quickCounts.completed}`}
                sx={{
                  fontWeight: 900,
                  borderRadius: "8px",
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                }}
              />

              {activeFilterChips.map((c) => (
                <Chip
                  key={c.k}
                  size="small"
                  label={c.label}
                  onDelete={() => clearChip(c.k)}
                  sx={{
                    fontWeight: 800,
                    borderRadius: "8px",
                    bgcolor: alpha(theme.palette.warning.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {/* Messages */}
      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: "8px" }}>
          {error}
        </Alert>
      </Collapse>
      <Collapse in={!!success}>
        <Alert severity="success" sx={{ mb: 1.5, borderRadius: "8px" }}>
          {success}
        </Alert>
      </Collapse>

      {/* DataGrid */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          overflow: "hidden",
          border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16)}`,
          bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
          boxShadow: "none",
        }}
      >
        {loading && <LinearProgress />}
        <Box sx={{ height: 660 }}>
          <DataGrid
            rows={displayRows}
            columns={columns}
            getRowId={(r) =>
              r?.id ??
              r?.appraisal_id ??
              r?.uuid ??
              `${r?.employee_id || r?.employee?.id || "emp"}-${
                r?.attributes?.period || r?.period || "p"
              }-${r?.attributes?.cycle || r?.cycle || "c"}`
            }
            rowCount={displayRowCount}
            loading={loading}
            paginationMode="server"
            pageSizeOptions={[10, 15, 25, 50, 100]}
            paginationModel={currentPaginationModel}
            onPaginationModelChange={handlePaginationChange}
            onRowClick={(params) => {
              setSelectedId(params.row?.id ?? params.row?.appraisal_id ?? null);
              setDetailsOpen(true);
            }}
            disableRowSelectionOnClick
            initialState={{
              sorting: {
                sortModel: [{ field: "id", sort: "desc" }],
              },
            }}
            slots={{ noRowsOverlay: NoRowsOverlay }}
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: alpha(theme.palette.action.hover, 0.5),
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 900 },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                bgcolor: alpha(theme.palette.action.hover, 0.18),
              },
              "& .MuiDataGrid-cell": {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
              },
            }}
          />
        </Box>
      </Paper>

      {/* Dialogs & Drawer */}
      <CreateAppraisalDialog
        open={createOpen}
        workflowTemplateOptions={workflowTemplateOptions}
        onClose={() => setCreateOpen(false)}
        onCreated={(msg) => {
          setSuccess(msg || "Appraisal created successfully");
          fetchAppraisals();
        }}
        onError={setError}
      />

      <BulkCreateAppraisalsDialog
        open={bulkOpen}
        workflowTemplateOptions={workflowTemplateOptions}
        onClose={() => setBulkOpen(false)}
        onDone={(msg) => {
          setSuccess(msg || "Bulk create completed");
          fetchAppraisals();
        }}
        onError={setError}
      />

      <EditAppraisalDialog
        open={editOpen}
        appraisalId={selectedId}
        workflowTemplateOptions={workflowTemplateOptions}
        onClose={() => setEditOpen(false)}
        onSaved={(msg) => {
          setSuccess(msg || "Appraisal updated successfully");
          fetchAppraisals();
        }}
        onError={setError}
      />

      <DeleteAppraisalDialog
        open={deleteOpen}
        appraisalId={selectedId}
        onClose={() => setDeleteOpen(false)}
        onDeleted={(msg) => {
          setSuccess(msg || "Appraisal deleted successfully");
          setDetailsOpen(false);
          fetchAppraisals();
        }}
        onError={setError}
      />

      <AppraisalDetailsDrawer
        open={detailsOpen}
        appraisalId={selectedId}
        onClose={() => setDetailsOpen(false)}
        onRefresh={fetchAppraisals}
        onRequestEdit={() => setEditOpen(true)}
        onRequestDelete={() => setDeleteOpen(true)}
      />
    </Box>
  );
}
